import { useEffect, useRef, useCallback } from 'react';
import { makeLogger } from '@/utils/logger/logger.ts';
import { PageNumberReasonChange } from '@/hooks/viewers/usePdfViewer.ts';

const logger = makeLogger('usePdfScrollSync');
const PAGE_SCROLL_RETRY_MS = 80;
const PAGE_SCROLL_MAX_ATTEMPTS = 16;
const PROGRAMMATIC_SCROLL_LOCK_MS = 120;

const clampPage = (page: number, totalPages: number): number => Math.max(1, Math.min(page, totalPages || page));

const getFallbackPageHeight = (container: HTMLDivElement, totalPages: number): number =>
	totalPages > 0 ? container.scrollHeight / totalPages : container.clientHeight || container.scrollHeight || 1;

const getFallbackPageForScroll = (container: HTMLDivElement, totalPages: number): number =>
	clampPage(Math.round(container.scrollTop / getFallbackPageHeight(container, totalPages)) + 1, totalPages);

interface UsePdfScrollSyncProps {
	currentPageNumber: number;
	totalPages: number;
	isOpen: boolean;
	loading: boolean;
	onPageNumberChange: (page: number, reason: PageNumberReasonChange) => void;
	setPageChangeReason: (callback: (reason: PageNumberReasonChange) => void) => void;
}

export const usePdfScrollSync = (props: UsePdfScrollSyncProps) => {
	const { currentPageNumber, totalPages, isOpen, loading, onPageNumberChange, setPageChangeReason } = props;

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const pageChangeReasonRef = useRef<PageNumberReasonChange>(null);

	// Use refs to store current values and avoid recreating the callback
	const currentPageNumberRef = useRef(currentPageNumber);
	const totalPagesRef = useRef(totalPages);
	const onPageNumberChangeRef = useRef(onPageNumberChange);
	const pageElementsRef = useRef<Map<number, HTMLElement>>(new Map());
	const scrollTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
	const programmaticScrollLockRef = useRef(false);

	const clearScrollTimeouts = useCallback(() => {
		scrollTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
		scrollTimeoutsRef.current.clear();
	}, []);

	const clearProgrammaticScroll = useCallback(() => {
		clearScrollTimeouts();
		programmaticScrollLockRef.current = false;
		if (pageChangeReasonRef.current === 'control') pageChangeReasonRef.current = null;
	}, [clearScrollTimeouts]);

	const scheduleScrollTimeout = useCallback((callback: () => void, delay: number) => {
		const timeout = setTimeout(() => {
			scrollTimeoutsRef.current.delete(timeout);
			callback();
		}, delay);
		scrollTimeoutsRef.current.add(timeout);
		return timeout;
	}, []);

	const registerPageRef = useCallback((page: number, element: HTMLElement | null) => {
		if (element) {
			pageElementsRef.current.set(page, element);
			return;
		}
		pageElementsRef.current.delete(page);
	}, []);

	// Update refs when props change
	useEffect(() => {
		currentPageNumberRef.current = currentPageNumber;
		totalPagesRef.current = totalPages;
		onPageNumberChangeRef.current = onPageNumberChange;
	}, [currentPageNumber, totalPages, onPageNumberChange]);

	// Reset page change reason when dialog opens/closes
	useEffect(() => {
		if (isOpen) {
			logger.info('Dialog opened, resetting page change reason');
			pageChangeReasonRef.current = null;
		} else {
			clearScrollTimeouts();
		}
	}, [clearScrollTimeouts, isOpen]);

	useEffect(() => clearScrollTimeouts, [clearScrollTimeouts]);

	// Register callback with the viewer hook to receive page change reason updates
	useEffect(() => {
		const handleReasonChange = (reason: PageNumberReasonChange) => {
			pageChangeReasonRef.current = reason;
			logger.info('Page change reason updated', { reason });
		};

		setPageChangeReason(handleReasonChange);
	}, [setPageChangeReason]);

	const scrollToPage = useCallback(
		(page: number, attempt = 0) => {
			const container = scrollContainerRef.current;

			if (!container || !isOpen || loading) {
				logger.info('Programmatic scroll skipped', { node: container, isOpen, loading });
				return;
			}

			const pageElement = pageElementsRef.current.get(page) ?? null;
			if (!pageElement || pageElement.offsetHeight === 0) {
				if (pageElementsRef.current.size > 0 && attempt < PAGE_SCROLL_MAX_ATTEMPTS) {
					scheduleScrollTimeout(() => scrollToPage(page, attempt + 1), PAGE_SCROLL_RETRY_MS);
					return;
				}
				const fallbackTarget = getFallbackPageHeight(container, totalPagesRef.current) * (page - 1);
				pageChangeReasonRef.current = 'control';
				programmaticScrollLockRef.current = true;
				container.scrollTo({ top: fallbackTarget, behavior: 'auto' });
				scheduleScrollTimeout(() => {
					programmaticScrollLockRef.current = false;
					pageChangeReasonRef.current = null;
				}, PROGRAMMATIC_SCROLL_LOCK_MS);
				return;
			}

			const containerRect = container.getBoundingClientRect();
			const pageRect = pageElement.getBoundingClientRect();
			const target = container.scrollTop + pageRect.top - containerRect.top;

			logger.info('Programmatic scroll to page', { currentPageNumber: currentPageNumberRef.current, target });

			pageChangeReasonRef.current = 'control';
			programmaticScrollLockRef.current = true;
			container.scrollTo({ top: target, behavior: 'auto' });

			scheduleScrollTimeout(() => {
				programmaticScrollLockRef.current = false;
				pageChangeReasonRef.current = null;
				logger.info('Programmatic scroll completed, reason cleared', {
					currentPageNumber: currentPageNumberRef.current,
				});
			}, PROGRAMMATIC_SCROLL_LOCK_MS);
		},
		[isOpen, loading, scheduleScrollTimeout],
	);

	// Create a stable handleUserScroll that doesn't recreate on every render
	const handleUserScroll = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		if (programmaticScrollLockRef.current || pageChangeReasonRef.current === 'control') {
			logger.info('Ignoring user scroll during control operation', { reason: pageChangeReasonRef.current });
			return;
		}

		const pageElements = Array.from(pageElementsRef.current.entries()).map(([page, element]) => ({
			page,
			element,
		}));
		if (pageElements.length === 0) {
			const visiblePage = getFallbackPageForScroll(container, totalPagesRef.current);
			if (visiblePage !== currentPageNumberRef.current) {
				logger.info('Page updated from fallback scroll', { page: visiblePage });
				pageChangeReasonRef.current = 'scroll';
				onPageNumberChangeRef.current(visiblePage, 'scroll');
			}
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const visiblePage =
			pageElements.reduce(
				(closestPage, pageElement) => {
					const distance = Math.abs(pageElement.element.getBoundingClientRect().top - containerRect.top);
					return distance < closestPage.distance ? { page: pageElement.page, distance } : closestPage;
				},
				{ page: currentPageNumberRef.current, distance: Number.POSITIVE_INFINITY },
			).page || currentPageNumberRef.current;

		logger.info('User scrolled', {
			scrollTop: container.scrollTop,
			page: visiblePage,
			currentPageNumber: currentPageNumberRef.current,
		});

		if (visiblePage !== currentPageNumberRef.current) {
			logger.info('Page updated from scroll', { page: visiblePage });
			pageChangeReasonRef.current = 'scroll';
			onPageNumberChangeRef.current(visiblePage, 'scroll');
		}
	}, []); // No dependencies - completely stable

	const registerScrollEvents = useCallback(() => {
		const container = scrollContainerRef.current;

		if (!container || !isOpen || loading) {
			logger.info('Scroll container not ready, skipping listener', {
				container,
				isOpen,
				totalPages: totalPagesRef.current,
				loading,
			});
			return;
		}

		logger.info('Scroll listener attached');
		container.addEventListener('scroll', handleUserScroll, { passive: true });
		container.addEventListener('wheel', clearProgrammaticScroll, { passive: true });
		container.addEventListener('touchstart', clearProgrammaticScroll, { passive: true });
		container.addEventListener('pointerdown', clearProgrammaticScroll, { passive: true });

		return () => {
			logger.info('Removing scroll listener');
			container.removeEventListener('scroll', handleUserScroll);
			container.removeEventListener('wheel', clearProgrammaticScroll);
			container.removeEventListener('touchstart', clearProgrammaticScroll);
			container.removeEventListener('pointerdown', clearProgrammaticScroll);
		};
	}, [clearProgrammaticScroll, handleUserScroll, loading, isOpen]); // handleUserScroll is now stable

	// Trigger programmatic scroll when page changes via buttons/inputs
	useEffect(() => {
		if (!pageChangeReasonRef.current) {
			logger.info('No page change reason, skipping programmatic scroll', {
				currentPageNumber: currentPageNumberRef.current,
			});
			return;
		}

		if (pageChangeReasonRef.current === 'scroll') {
			logger.info('Skipping programmatic scroll due to user scroll action', {
				currentPageNumber: currentPageNumberRef.current,
			});
			return;
		}

		logger.info('Triggering programmatic scroll', {
			currentPageNumber: currentPageNumberRef.current,
			pageChangeReason: pageChangeReasonRef.current,
		});
		scrollToPage(currentPageNumber);
	}, [currentPageNumber, scrollToPage]);

	useEffect(() => {
		if (!isOpen || loading || currentPageNumber <= 1) return;
		if (pageChangeReasonRef.current === 'scroll') return;
		const frame = requestAnimationFrame(() => scrollToPage(currentPageNumber));
		return () => cancelAnimationFrame(frame);
	}, [currentPageNumber, isOpen, loading, scrollToPage]);

	return { scrollContainerRef, scrollToPage, registerScrollEvents, registerPageRef };
};
